import React, { forwardRef, isValidElement, type ReactElement, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Content } from "@radix-ui/react-tabs";
import c from "classnames/bind";

import styles from "./tab-content.module.scss";

export type TabContentProps = Omit<BaseProps, "children"> & {
  /**
   * Content to be rendered inside a `button` element.
   * Only pass ReactNodes that are valid as children of a `button`, such as plain text,
   * inline elements like `span` or other non-interactive elements. Avoid block-level or interactive elements.
   */
  name: ReactNode;
  /**
   * A unique ID that associates the tab with a content.
   * */
  tabId: string;
  /**
   * If true, the inactive tab content will not be rendered.
   */
  disableForceMount?: boolean;
  children: ReactNode;
};

const cx = c.bind(styles);
const rootClassName = "purpur-tab-content";

export const TabContent = forwardRef<HTMLDivElement, TabContentProps>(
  ({ children, tabId, "data-testid": dataTestId, className, disableForceMount, ...props }, ref) => (
    <Content
      ref={ref}
      className={cx(rootClassName, className, {
        [`${rootClassName}--force-mount`]: !disableForceMount,
      })}
      data-testid={dataTestId}
      value={tabId}
      forceMount={!disableForceMount || undefined}
      tabIndex={0}
      {...props}
    >
      {children}
    </Content>
  )
);

export const isTabContent = (child?: ReactNode): child is ReactElement<TabContentProps> =>
  !!child && isValidElement<TabContentProps>(child) && !!child.props.name && !!child.props.tabId;
